/**
 * **Staf, peran & PIN** — pemblokir onboarding kedua (Imron, 2026-08-04).
 *
 * ### ⛔⛔ Tanpa layar ini, tenant baru tak punya satu pun kasir yang bisa login
 * Metode bayar membuka gerbang kesiapan; layar ini membuka **pintunya**. Sebelum ada keduanya,
 * tenant yang baru di-onboard bisa menata seluruh menu dan outletnya lalu mentok — dan satu-satunya
 * jalan keluar adalah SQL langsung.
 *
 * ### ⭐⭐ Izin TIDAK menempel pada orang — ia menempel pada JABATAN
 * Rantainya: `staf → jabatan → peran → izin`. Itu bukan kerumitan yang ditambahkan; itu yang
 * membuat "kasir baru masuk" jadi satu baris data alih-alih daftar centang yang diulang tiap orang
 * — dan daftar centang yang diulang **selalu** menyimpang: satu orang kelebihan izin, dan yang
 * menemukannya adalah audit.
 *
 * ⚠️ Karena itu layar ini menampilkan seluruh rantainya, bukan cuma ujungnya. Manajer yang melihat
 * "Rani tak bisa void" harus bisa menelusuri ke mana harus memperbaikinya tanpa menebak.
 *
 * ### ⛔ Batas K8 tetap berlaku
 * Tak ada gaji, KTP, atau foto — dan itu bukan kealpaan. POS tak berhak menyimpannya, dan
 * menyimpannya berarti menanggung kewajiban perlindungan data yang tak memberi manfaat apa pun
 * bagi kasir.
 */
import { Alert, Button, Card, Form, Input, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import { canWrite } from '../api/session';
import { CrudPanel } from '../components/CrudPanel';

interface StaffRow {
  readonly id: string;
  readonly hr_employee_code: string;
  readonly display_name: string;
  readonly position_code: string | null;
  readonly hr_source: string;
  readonly is_active: boolean;
}
interface RoleRow { readonly id: string; readonly code: string; readonly name: string }
interface RolePermRow { readonly id: string; readonly pos_role_id: string; readonly permission_code: string }
interface PositionRoleRow { readonly id: string; readonly position_code: string; readonly pos_role_id: string }

/**
 * ⛔ Daftar izin. ⚠️ Disalin dari `@osbpos/contract` **dengan sadar** karena portal-web tak
 * memuatnya sebagai dependensi; kalau kelak ia memuatnya, ambil dari sana. Yang tak boleh terjadi:
 * daftar ini menua diam-diam. Uji struktural di portal-api (MS14/MS15) menjaga sisi server; di sini
 * yang menjaga adalah bahwa server **menolak** kode izin yang tak dikenal.
 */
const PERMISSIONS = [
  'order:create', 'order:void', 'order:refund', 'order:reopen', 'order:takeover',
  'payment:take', 'drawer:open', 'order:discount', 'promo:approve',
  'shift:open', 'shift:close', 'day:close', 'master:edit', 'report:view',
] as const;

export function Staff() {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [roles, setRoles] = useState<readonly RoleRow[]>([]);
  const [perms, setPerms] = useState<readonly RolePermRow[]>([]);
  const [posRoles, setPosRoles] = useState<readonly PositionRoleRow[]>([]);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [pinFor, setPinFor] = useState<StaffRow | null>(null);
  const [pin, setPin] = useState('');
  const [tick, setTick] = useState(0);

  const muatPeran = useCallback(async () => {
    const [r, p, pr] = await Promise.all([
      api.listMaster<RoleRow>('roles'),
      api.listMaster<RolePermRow>('role-permissions'),
      api.listMaster<PositionRoleRow>('position-roles'),
    ]);
    setRoles(r);
    setPerms(p);
    setPosRoles(pr);
    setRoleId((cur) => cur ?? r[0]?.id ?? null);
  }, []);

  useEffect(() => { void muatPeran().catch(() => undefined); }, [muatPeran, tick]);

  const izinPeran = new Set(perms.filter((x) => x.pos_role_id === roleId).map((x) => x.permission_code));

  async function ubahIzin(code: string, nyala: boolean): Promise<void> {
    if (roleId === null) return;
    try {
      if (nyala) {
        await api.createMaster('role-permissions', { posRoleId: roleId, permissionCode: code });
      } else {
        const baris = perms.find((x) => x.pos_role_id === roleId && x.permission_code === code);
        if (baris !== undefined) await api.deleteMaster('role-permissions', baris.id);
      }
      setTick((n) => n + 1);
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    }
  }

  const kolomStaf: ColumnsType<StaffRow> = [
    {
      title: t('staff.code'),
      dataIndex: 'hr_employee_code',
      width: 140,
      render: (v: string) => <b data-testid={`staf-${v}`}>{v}</b>,
    },
    { title: t('master.name'), dataIndex: 'display_name' },
    {
      /*
       * ⭐⭐ Jabatan tampil DI TABEL, dan izin efektifnya ikut. "Kenapa Rani tak bisa void" adalah
       * pertanyaan yang datang di tengah jam sibuk — menjawabnya dengan membuka tiga layar berarti
       * jawabannya tak pernah benar-benar dicari, dan yang terjadi berikutnya adalah semua orang
       * diberi peran supervisor.
       */
      title: t('staff.position'),
      dataIndex: 'position_code',
      width: 220,
      render: (v: string | null) => {
        if (v === null || v === '') {
          /* ⛔ Tanpa jabatan, staf ini TAK PUNYA izin apa pun — dan itu harus terlihat, bukan
             ditemukan saat ia gagal login di depan antrean. */
          return <Tag color="red" data-testid="staf-tanpa-jabatan">{t('staff.noPosition')}</Tag>;
        }
        const map = posRoles.find((x) => x.position_code === v);
        const peran = roles.find((r) => r.id === map?.pos_role_id);
        return (
          <Space size={4} wrap>
            <Tag>{v}</Tag>
            {peran === undefined
              ? <Tag color="orange" data-testid={`staf-jabatan-tanpa-peran-${v}`}>{t('staff.positionUnmapped')}</Tag>
              : <Tag color="blue">{peran.code}</Tag>}
          </Space>
        );
      },
    },
    {
      title: t('staff.hrSource'),
      dataIndex: 'hr_source',
      width: 120,
      render: (v: string) => <Tag>{t(`staff.source.${v}`)}</Tag>,
    },
    {
      title: t('staff.pin'),
      key: 'pin',
      width: 120,
      render: (_: unknown, r: StaffRow) =>
        boleh ? (
          <Button size="small" data-testid={`staf-pin-${r.hr_employee_code}`}
                  onClick={(e) => { e.stopPropagation(); setPinFor(r); setPin(''); }}>
            {t('staff.setPin')}
          </Button>
        ) : null,
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="staff">
      <Alert type="info" showIcon data-testid="staf-rantai" title={t('staff.chain')} />

      <CrudPanel<StaffRow>
        testId="staf"
        historyPath="staff"
        title={t('staff.title')}
        subtitle={t('staff.subtitle')}
        canWrite={boleh}
        newLabel={t('staff.new')}
        columns={kolomStaf}
        fetchList={() => api.listMaster<StaffRow>('staff', { includeInactive: 'true' })}
        searchIn={(s) => `${s.hr_employee_code} ${s.display_name} ${s.position_code ?? ''}`}
        emptyText={t('staff.empty')}
        reloadToken={tick}
        renderFields={(_f, editing) => (
          <>
            {editing === null && (
              <Form.Item name="hrEmployeeCode" label={t('staff.code')}
                         rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/, message: t('structure.codeRule') }]}
                         tooltip={t('structure.codeFixed')}>
                <Input maxLength={40} data-testid="staf-kode" />
              </Form.Item>
            )}
            <Form.Item name="displayName" label={t('master.name')} rules={[{ required: true, min: 2 }]}>
              <Input maxLength={120} data-testid="staf-nama" />
            </Form.Item>
            {/*
              ⭐⭐ Jabatan adalah SATU-SATUNYA jalan izin sampai ke orang. Dikosongkan = staf tanpa
              izin apa pun — sah (mis. staf yang belum mulai), tapi harus dipilih sadar.
            */}
            <Form.Item name="positionCode" label={t('staff.position')} tooltip={t('staff.positionHint')}>
              <Select
                allowClear
                data-testid="staf-jabatan"
                options={posRoles.map((p) => ({
                  value: p.position_code,
                  label: `${p.position_code} → ${roles.find((r) => r.id === p.pos_role_id)?.code ?? '?'}`,
                }))}
              />
            </Form.Item>
            {/* ⛔ K8: tanpa gaji, KTP, foto — POS tak berhak menyimpannya. */}
            <Alert type="info" showIcon title={t('staff.k8')} />
          </>
        )}
        toFormValues={(s) => ({
          displayName: s.display_name,
          positionCode: s.position_code ?? undefined,
        })}
        onCreate={async (v) => {
          await api.createMaster('staff', {
            hrEmployeeCode: String(v['hrEmployeeCode']).toUpperCase(),
            displayName: String(v['displayName']),
            /* ⭐ `local` = dibuat di portal ⇒ POS pemilik catatannya (K8 diamandemen). */
            hrSource: 'local',
            ...(v['positionCode'] === undefined || v['positionCode'] === null
              ? {} : { positionCode: String(v['positionCode']) }),
          });
        }}
        onUpdate={async (id, v) => {
          await api.updateMaster('staff', id, {
            displayName: v['displayName'],
            positionCode: v['positionCode'] ?? null,
          });
        }}
      />

      {/* ── peran & izin ────────────────────────────────────────────────── */}
      <Card title={t('staff.roles')} data-testid="peran-kartu"
            extra={
              <Select<string>
                style={{ minWidth: 220 }}
                {...(roleId === null ? {} : { value: roleId })}
                onChange={(v) => { setRoleId(v); }}
                data-testid="peran-pilih"
                options={roles.map((r) => ({ value: r.id, label: `${r.code} — ${r.name}` }))}
              />
            }>
        {roles.length === 0 ? (
          /* ⛔ Tenant baru lahir TANPA peran sama sekali; dikatakan apa adanya. */
          <Alert type="warning" showIcon data-testid="peran-kosong" title={t('staff.noRole')} />
        ) : (
          <Table<{ code: string }>
            size="small"
            pagination={false}
            rowKey={(r) => r.code}
            data-testid="peran-izin"
            dataSource={PERMISSIONS.map((code) => ({ code }))}
            columns={[
              {
                title: t('staff.permission'),
                dataIndex: 'code',
                render: (v: string) => <span data-testid={`izin-${v}`}>{v}</span>,
              },
              {
                title: '',
                key: 'aksi',
                width: 120,
                render: (_: unknown, r: { code: string }) => {
                  const punya = izinPeran.has(r.code);
                  return (
                    <Button
                      size="small"
                      type={punya ? 'primary' : 'default'}
                      disabled={!boleh}
                      data-testid={`izin-tombol-${r.code}`}
                      onClick={() => void ubahIzin(r.code, !punya)}
                    >
                      {punya ? t('common.on') : t('common.off')}
                    </Button>
                  );
                },
              },
            ]}
          />
        )}
      </Card>

      {/*
        ⛔ Ganti PIN, bukan LIHAT PIN. Yang tersimpan hash-nya; layar tak pernah bisa
        menampilkannya — dan itu memang benar. PIN yang bisa dilihat manajer adalah PIN yang
        dipakai manajer.
      */}
      <Modal
        open={pinFor !== null}
        title={t('staff.setPinFor', { name: pinFor?.display_name ?? '' })}
        okText={t('crud.save')}
        cancelText={t('crud.cancel')}
        onCancel={() => { setPinFor(null); }}
        onOk={() => {
          void (async () => {
            if (pinFor === null) return;
            try {
              await api.setStaffPin(pinFor.id, pin);
              void message.success(t('crud.saved'));
              setPinFor(null);
            } catch (e) {
              void message.error(e instanceof Error ? e.message : String(e));
            }
          })();
        }}
      >
        <Typography.Paragraph type="secondary">{t('staff.pinHint')}</Typography.Paragraph>
        <Input.Password
          value={pin}
          inputMode="numeric"
          maxLength={8}
          data-testid="staf-pin-isi"
          onChange={(e) => { setPin(e.target.value); }}
        />
      </Modal>
    </Space>
  );
}
